<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
            {{ t("reports.myReports") }}
        </h1>

        <!-- Loading State -->
        <div
            v-if="loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="4" :cols="6" />
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <p class="text-sm text-danger-700 dark:text-danger-300">
                {{ error }}
            </p>
        </div>

        <!-- Data Table -->
        <div
            v-if="!loading && !error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("reports.academicYear") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("reports.semester") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("reports.finalScore") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("reports.rank") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("common.actions") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="report in reports"
                            :key="report.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{
                                    report.semester?.academic_year?.name || "-"
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ report.semester?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white"
                            >
                                {{
                                    report.final_score !== null &&
                                    report.final_score !== undefined
                                        ? `${report.final_score} (${report.grade})`
                                        : "-"
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{
                                    report.rank_in_class
                                        ? `${report.rank_in_class} / ${report.total_students}`
                                        : "-"
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="viewReport(report)"
                                    :title="t('reports.viewDetail')"
                                    class="inline-flex items-center justify-center p-2 text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300 rounded-md hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors mr-2"
                                >
                                    <Eye class="w-4 h-4" />
                                </button>
                                <button
                                    @click="downloadPdf(report)"
                                    :title="t('reports.downloadPdf')"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                                >
                                    <Download class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <EmptyState
                v-if="!loading && reports.length === 0"
                icon="FileTextIcon"
                :title="t('reports.noReports')"
                :description="t('reports.noReportsHint')"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Eye, Download } from "@lucide/vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import reportCardApi, { ReportCard } from "@/api/reportCard";

const { t } = useI18n();
const router = useRouter();

const reports = ref<ReportCard[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

async function fetchReports() {
    loading.value = true;
    error.value = null;

    try {
        reports.value = await reportCardApi.getMy();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch report cards";
    } finally {
        loading.value = false;
    }
}

function viewReport(report: ReportCard) {
    router.push(`/student-reports/${report.id}`);
}

function downloadPdf(report: ReportCard) {
    window.open(reportCardApi.getPdfStreamUrl(report.id), "_blank");
}

onMounted(() => {
    fetchReports();
});
</script>
